import React from 'react';

// ── Strip internal AI command tags Sara sends to the Telegram bot ──────────
const INTERNAL_CMD_REGEX = /\[(?:SAVE_NAME|ALERT_SET|ALERT_CLEAR|SET_PORTFOLIO|PORTFOLIO_SUMMARY|SIGNAL|NOTE|TAG)[^\]]*\]/g;

export function cleanMessage(text: string): string {
    return (text || '')
        .replace(INTERNAL_CMD_REGEX, '')   // remove [CMD: ...] tags
        // Strip Gemini thinking/reasoning blocks (various formats)
        .replace(/<think>[\s\S]*?<\/think>/gi, '')      // <think>...</think> tags
        .replace(/^thought\b[\s\S]*?(?=\n[^\n]|\n\n|$)/im, '') // "thought" block at start
        // Collapse degenerate loops of dashes/hyphens (40+ characters) to prevent runaway model output
        .replace(/-{40,}/g, '---')
        .replace(/\n{3,}/g, '\n\n')        // collapse excessive blank lines
        .trim();
}

// ── Convert advanced markdown to styled React Nodes (Matching EXE & Dashboard quality) ──
export const renderMessageText = (text: string): React.ReactNode[] => {
  const cleanText = cleanMessage(text);
  const lines = cleanText.split('\n');
  const renderedContent: React.ReactNode[] = [];
  let currentTable: string[][] = [];
  let inTable = false;

  const parseInlineMarkdown = (htmlLine: string) => {
    // Match bold, telegram bot, markdown links, URLs, and standalone stock codes (4 digits)
    const parts = htmlLine.split(/(\*\*.*?\*\*|@TAD_VIP_bot|\[.*?\]\(.*?\)|\bhttps?:\/\/[^\s"'<>]+|\b[a-zA-Z0-9.-]+\.(?:com|org|net|sa)\b|\b[1-8]\d{3}\b)/g);
    return parts.map((part, i) => {
        if (!part) return null;
        if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={i} className="text-[#E9D5FF] mx-0.5 font-bold">{part.slice(2, -2)}</strong>;
        }
        if (part === '@TAD_VIP_bot') {
            return <a key={i} href="https://t.me/TAD_VIP_bot" target="_blank" rel="noopener noreferrer" className="text-[#C084FC] font-bold hover:underline mx-1" dir="ltr" style={{ display: 'inline-block' }}>@TAD_VIP_bot</a>;
        }
        const mdLinkMatch = part.match(/^\[(.*?)\]\((.*?)\)$/);
        if (mdLinkMatch) {
            const url = mdLinkMatch[2];
            const isMailOrTel = url.startsWith('mailto:') || url.startsWith('tel:');
            const href = (url.startsWith('http') || isMailOrTel) ? url : `https://${url}`;
            return <a key={i} href={href} target={isMailOrTel ? undefined : "_blank"} rel="noopener noreferrer" className="text-[#38bdf8] font-bold hover:underline mx-1" dir="ltr">{mdLinkMatch[1]}</a>;
        }
        if (part.match(/^(https?:\/\/[^\s"'<>]+|[a-zA-Z0-9.-]+\.(?:com|org|net|sa))$/)) {
            const href = part.startsWith('http') ? part : `https://${part}`;
            return <a key={i} href={href} target="_blank" rel="noopener noreferrer" className="text-[#38bdf8] font-bold hover:underline mx-1" dir="ltr">{part}</a>;
        }
        // Verified Saudi Stock Code badge (4 digits starting 1-8)
        if (part.match(/^[1-8]\d{3}$/)) {
            return (
                <span key={i} className="inline-flex items-center px-1.5 py-0.5 mx-0.5 rounded bg-[#8B5CF6]/20 text-[#C084FC] font-mono font-bold text-xs border border-[#8B5CF6]/40 select-all" dir="ltr">
                    {part}
                </span>
            );
        }
        return <span key={i}>{part}</span>;
    });
  };

  const isTableRow = (l: string) => {
    const trimmed = l.trim();
    if (!trimmed.includes('|')) return false;
    const pipeCount = (trimmed.match(/\|/g) || []).length;
    return pipeCount >= 2;
  };

  const isSeparatorRow = (cells: string[]) => {
    return cells.length > 0 && cells.every(c => /^:?[-—=]+:?$/.test(c.trim()) || c.trim() === '');
  };

  const renderTableBlock = (key: string, rows: string[][]) => {
    if (!rows || rows.length === 0) return null;
    const header = rows[0];
    const dataRows = rows.slice(1);

    if (dataRows.length === 0) {
      return (
        <div key={key} className="my-2 p-2 rounded-lg bg-[#8B5CF6]/10 text-[#C084FC] font-semibold text-xs flex flex-wrap gap-2">
          {header.map((c, i) => (
            <span key={i} className="bg-white/10 px-2 py-1 rounded">{parseInlineMarkdown(c)}</span>
          ))}
        </div>
      );
    }

    // Mobile: one card per row so wide comparison tables stay readable without horizontal scroll.
    const mobileCards = (
      <div className="sm:hidden my-3 space-y-2">
        {dataRows.map((row, i) => (
          <div key={i} className="rounded-xl border border-[#8B5CF6]/30 bg-[#0d152b]/80 p-3 shadow-md">
            <div className="text-[#C084FC] font-bold text-sm mb-2 pb-1.5 border-b border-[#8B5CF6]/20">
              {parseInlineMarkdown(row[0] || header[0] || '')}
            </div>
            <div className="space-y-1">
              {row.map((cell, j) => {
                if (j === 0) return null;
                const label = header[j] ?? '';
                return (
                  <div key={j} className="flex items-start justify-between gap-2 text-xs leading-relaxed">
                    <span className="text-gray-400 shrink-0">{parseInlineMarkdown(label)}</span>
                    <span className="text-gray-100 text-left font-semibold">{parseInlineMarkdown(cell)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );

    return (
      <>
        {mobileCards}
        <div key={key} className="hidden sm:block overflow-x-auto my-3 border border-[#8B5CF6]/30 rounded-xl bg-[#0d152b]/80 shadow-md">
          <table className="w-full text-sm text-right border-collapse">
            <thead>
              <tr className="bg-[#8B5CF6]/20 border-b border-[#8B5CF6]/30">
                {header.map((cell, i) => (
                  <th key={i} className="p-2.5 font-bold text-[#C084FC] text-xs sm:text-sm whitespace-nowrap">
                    {parseInlineMarkdown(cell)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dataRows.map((row, i) => (
                <tr key={i} className={`border-b border-[#8B5CF6]/10 transition-colors ${i % 2 === 1 ? 'bg-white/[0.02]' : ''} hover:bg-[#8B5CF6]/10`}>
                  {row.map((cell, j) => (
                    <td key={j} className="p-2.5 text-gray-200 text-xs sm:text-sm leading-relaxed">
                      {parseInlineMarkdown(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  const processLine = (line: string, idx: number) => {
    const htmlLine = line.trim();
    if (!htmlLine) return null;

    // Horizontal Rule (---, ===, ___)
    if (/^[-—=_]{3,}$/.test(htmlLine)) {
      return <hr key={idx} className="my-3 border-t border-[#8B5CF6]/30" />;
    }

    // Special Technical Verdict Card
    const isVerdict = htmlLine.startsWith('القرار الفني:') || 
                      htmlLine.startsWith('الترجيح الفني:') || 
                      htmlLine.startsWith('النظرة الفنية:') ||
                      htmlLine.startsWith('التوصية:') ||
                      htmlLine.startsWith('الرأي الفني:') ||
                      htmlLine.startsWith('الاتجاه العام:') ||
                      htmlLine.startsWith('🟢') ||
                      htmlLine.startsWith('🔴') ||
                      htmlLine.startsWith('🟡');
    if (isVerdict) {
      const isPositive = htmlLine.includes('صاعد') || htmlLine.includes('إيجاب') || htmlLine.includes('شراء') || htmlLine.includes('🟢');
      const isNegative = htmlLine.includes('هابط') || htmlLine.includes('سلب') || htmlLine.includes('بيع') || htmlLine.includes('🔴');
      const badgeBg = isPositive 
        ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-400'
        : isNegative 
          ? 'bg-rose-500/15 border-rose-500/40 text-rose-400'
          : 'bg-amber-500/15 border-amber-500/40 text-amber-400';
      return (
        <div key={idx} className={`my-2 p-2.5 rounded-lg border text-sm font-semibold flex items-center gap-2 ${badgeBg}`}>
          <span>{isPositive ? '🟢' : isNegative ? '🔴' : '🟡'}</span>
          <span>{parseInlineMarkdown(htmlLine.replace(/^[🟢🔴🟡]\s*/, ''))}</span>
        </div>
      );
    }

    // Headings (#, ##, ###)
    if (htmlLine.startsWith('### ')) {
      return (
        <div key={idx} className="text-base font-bold text-[#C084FC] mt-3 mb-1">
          {parseInlineMarkdown(htmlLine.substring(4))}
        </div>
      );
    }
    if (htmlLine.startsWith('## ')) {
      return (
        <div key={idx} className="text-lg font-bold text-[#E9D5FF] mt-4 mb-2">
          {parseInlineMarkdown(htmlLine.substring(3))}
        </div>
      );
    }
    if (htmlLine.startsWith('# ')) {
      return (
        <div key={idx} className="text-xl font-extrabold text-[#F3E8FF] mt-4 mb-2">
          {parseInlineMarkdown(htmlLine.substring(2))}
        </div>
      );
    }

    // List item (-, *, •, or numbered 1. 2.)
    if (htmlLine.startsWith('- ') || htmlLine.startsWith('* ') || htmlLine.startsWith('• ')) {
      const bulletContent = htmlLine.substring(2);
      return (
        <li key={idx} className="list-disc mr-4 mb-1 text-gray-200 text-sm leading-relaxed">
          {parseInlineMarkdown(bulletContent)}
        </li>
      );
    }
    const numMatch = htmlLine.match(/^(\d+)\.\s+(.*)/);
    if (numMatch) {
      return (
        <div key={idx} className="mr-2 mb-1.5 text-gray-200 text-sm leading-relaxed flex items-start gap-1.5">
          <span className="text-[#C084FC] font-bold shrink-0">{numMatch[1]}.</span>
          <span>{parseInlineMarkdown(numMatch[2])}</span>
        </div>
      );
    }

    // Standard Paragraph Text
    return (
      <div key={idx} className="mb-1 text-sm leading-relaxed text-gray-100">
        {parseInlineMarkdown(htmlLine)}
      </div>
    );
  };

  lines.forEach((line, idx) => {
    const trimmed = line.trim();

    if (isTableRow(line)) {
      const cells = trimmed.replace(/^\||\|$/g, '').split('|').map(c => c.trim());
      if (isSeparatorRow(cells)) return;

      if (!inTable) {
        inTable = true;
        currentTable = [cells];
      } else {
        currentTable.push(cells);
      }
    } else {
      if (inTable) {
        renderedContent.push(renderTableBlock(`table-${idx}`, currentTable));
        inTable = false;
        currentTable = [];
      }
      if (trimmed) {
        const elem = processLine(line, idx);
        if (elem) renderedContent.push(elem);
      }
    }
  });

  if (inTable && currentTable.length > 0) {
    renderedContent.push(renderTableBlock('table-final', currentTable));
  }

  return renderedContent;
};
